/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useMutation, type MutationHookOptions } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Paths from '@fai/friendli-suite/features/routes/paths';
import {
  DedicatedFinetuningDeleteDocument,
  type DedicatedFinetuningDeleteMutation,
  type DedicatedFinetuningDeleteMutationVariables,
} from '@fai/friendli-suite/generated/client/graphql';
import { errorProps, successProps } from '@fai/ui/components/notification/props';

export const useDeleteFinetuning = (
  mutationOptions?: MutationHookOptions<
    DedicatedFinetuningDeleteMutation,
    DedicatedFinetuningDeleteMutationVariables
  >
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { teamId = '', projectId = '', finetuningId = '' } = useParams();

  const [deleteFinetuningMutation, { loading }] = useMutation(DedicatedFinetuningDeleteDocument, {
    ...mutationOptions,
    onCompleted: (response) => {
      switch (response.dedicatedFinetuningDelete.__typename) {
        case 'DedicatedFinetuningDeleteSuccess': {
          notifications.show({
            ...successProps,
            message: t('dedicated_endpoints.project.finetuning_job_deleted_success'),
          });

          navigate(Paths.team(teamId).dedicatedEndpoints.project.detail(projectId).finetuning.list);

          break;
        }

        case 'UserPermissionError':
        case 'TeamNotExistError': {
          notifications.show({
            ...errorProps,
            message: response.dedicatedFinetuningDelete.message,
          });

          break;
        }

        default: {
          notifications.show({
            ...errorProps,
            message: t('dedicated_endpoints.project.delete_error'),
          });

          break;
        }
      }

      if (mutationOptions?.onCompleted) {
        mutationOptions.onCompleted(response);
      }
    },
    update: (cache) => {
      cache.evict({
        id: cache.identify({
          __typename: 'DedicatedProject',
          id: projectId,
        }),
        fieldName: 'finetunings',
      });
    },
  });

  const deleteFinetuning = () => {
    deleteFinetuningMutation({
      variables: { input: { teamId, projectId, finetuningId } },
    });
  };

  return { deleteFinetuning, loading };
};
